import type { Permission } from "@/shared/auth/permissions";
import { type FeatureKey, resolveFeatureLabel } from "@/shared/entitlements/entitlements.constants";
import {
  type ImportField,
  type ImportGroup,
  type ImportGroupKey,
  type ImportSectionKey,
  importGroup,
} from "@/shared/import/import-groups";
import { planUpgradeFeature } from "@/shared/lib/plan-upgrade-error";

/// What the caller may import: their permissions and their account's plan.
/// One object rather than two optional predicates so a caller cannot pass the
/// permission check and silently skip the plan check.
export interface ImportAccess {
  readonly isGranted: (permission: Permission) => boolean;
  readonly isEnabled: (feature: FeatureKey) => boolean;
}

export const NO_GATED_ACCESS: ImportAccess = {
  isGranted: () => false,
  isEnabled: () => false,
};

/**
 * Narrows a group's catalogue to what the caller may import.
 *
 * The shore fails the whole file when a column claimed by a handler arrives
 * from a caller without that handler's permission, or from an account whose
 * plan lacks the handler's feature (403 PLAN_UPGRADE_REQUIRED). So a gated
 * field is offered only when BOTH hold, and is never suggested or written
 * into the rewritten CSV otherwise. Every consumer of the mapping
 * (suggestion, required check, rewrite) reads the group this returns, never
 * the raw catalogue. `alsoSendAs` copies are gated on their own permission,
 * independently of the field that carries them.
 */
export function resolveImportGroup(group: ImportGroup, access: ImportAccess): ImportGroup {
  const allowed = (requires: Permission | undefined): boolean =>
    requires === undefined || access.isGranted(requires);
  const fields: ImportField[] = [];
  for (const field of group.fields) {
    if (!allowed(field.requires)) continue;
    if (field.requiresFeature !== undefined && !access.isEnabled(field.requiresFeature)) continue;
    if (field.alsoSendAs === undefined) {
      fields.push(field);
      continue;
    }
    const copies = field.alsoSendAs.filter((copy) => allowed(copy.requires));
    fields.push({ ...field, alsoSendAs: copies });
  }
  return { ...group, fields };
}

const SECTION_ORDER: readonly ImportSectionKey[] = [
  "basics",
  "job",
  "personal",
  "exit",
  "workLocation",
  "compensation",
  "bankStatutory",
];

export const SECTION_LABELS: Readonly<Record<ImportSectionKey, string>> = {
  basics: "Basics",
  job: "Job",
  personal: "Personal",
  exit: "Exit",
  workLocation: "Work location",
  compensation: "Compensation",
  bankStatutory: "Bank and statutory",
};

export interface ImportFieldSection {
  /** Null for fields that carry no section (the payroll catalogue). */
  readonly key: ImportSectionKey | null;
  readonly fields: readonly ImportField[];
}

/**
 * Buckets fields for display in a fixed section order, keeping catalogue
 * order inside each section. Empty sections are dropped, so a caller without
 * HR access sees no Personal heading. Unsectioned fields form one leading
 * bucket with a null key, which is how payroll keeps its flat list.
 */
export function sectionImportFields(fields: readonly ImportField[]): ImportFieldSection[] {
  const unsectioned = fields.filter((field) => field.section === undefined);
  const sections: ImportFieldSection[] = [];
  if (unsectioned.length > 0) sections.push({ key: null, fields: unsectioned });
  for (const key of SECTION_ORDER) {
    const inSection = fields.filter((field) => field.section === key);
    if (inSection.length > 0) sections.push({ key, fields: inSection });
  }
  return sections;
}

/**
 * A readable message for a failed dry run or commit. The shore's plan refusal
 * says only "This feature is not included in your plan.", which does not tell
 * the user which of their mapped columns caused it. When the refused feature
 * gates fields of this group, name the sections those fields sit in; any
 * other plan refusal (the seat cap reports `max_users`) keeps the shore's
 * own message, which already describes it.
 */
export function describeImportFailure(
  groupKey: ImportGroupKey,
  err: unknown,
  fallback: string,
): string {
  const group = importGroup(groupKey);
  const feature = planUpgradeFeature(err);
  const gated = group.fields.find((field) => field.requiresFeature === feature);
  if (gated?.requiresFeature !== undefined) {
    const sections = new Set<string>();
    for (const field of group.fields) {
      if (field.requiresFeature === gated.requiresFeature && field.section !== undefined) {
        sections.add(SECTION_LABELS[field.section]);
      }
    }
    const columns = sections.size > 0 ? `${[...sections].join(" and ")} columns` : "Some columns";
    return `${columns} need ${resolveFeatureLabel(gated.requiresFeature)}, which your plan does not include. Unmap them to import the rest, or upgrade your plan.`;
  }
  return err instanceof Error && err.message !== "" ? err.message : fallback;
}
